import type { PortableTextBlock } from "sanity";
import type { IPricingV2Discount } from "@/sanity/templates";
import type { AffiliateFundingType } from "@/lib/affiliateDiscount";

/**
 * The offer changed from a split discount ("80% auf Challenges, 40% auf Instant
 * Funding") to a single percentage covering both products. The page documents
 * still hold the old split copy, so it is rewritten here at render time and the
 * percentage comes from withAffiliateOfferDiscount: Affiliate Discount Settings
 * while it is active, else the Pricing Section V3 discount.
 *
 * Everything is keyed off `resolveOfferLocale` finding the old wording and
 * `getNewOfferPercent` returning a value: if either is missing the rewriters are
 * no-ops and the page renders its stored copy unchanged.
 */

export type OfferLocale = "de" | "en";

/**
 * Core ≈ Challenges, Instant Funding ≈ Instant Funding. Both are configured to
 * the same value; `Math.min` is a guard so a mismatched pair can never advertise
 * a larger discount than checkout actually grants.
 */
export function getNewOfferPercent(
  pricingV2: IPricingV2Discount | null | undefined,
  fundingType: AffiliateFundingType,
): number | undefined {
  if (!pricingV2) return undefined;
  const core =
    fundingType === "futures"
      ? pricingV2.futuresCoreDiscountPercent
      : pricingV2.cfdCryptoCoreDiscountPercent;
  const instantFunding =
    fundingType === "futures"
      ? pricingV2.futuresInstantFundingDiscountPercent
      : pricingV2.cfdCryptoInstantFundingDiscountPercent;
  const set = [core, instantFunding].filter(
    (value): value is number => typeof value === "number" && value > 0,
  );
  return set.length ? Math.min(...set) : undefined;
}

// The stored copy is structurally consistent but noisy: five different separators
// between the two products (und/and , & • |), inconsistent spacing and casing
// around "%", and an optional "Rabatt".
//
// All patterns are non-global on purpose: `.test()` on a /g regex is stateful
// (lastIndex), and each of these fields holds at most one occurrence.
const PERCENT = String.raw`\d+(?:[.,]\d+)?`;
const SEPARATOR = String.raw`(?:und|and|,|&|•|\|)`;
const DE_OFF = String.raw`(?:Rabatt\s+)?auf`;
const EN_OFF = String.raw`(?:discount\s+)?off`;

const buildPair = (off: string) =>
  new RegExp(
    `${PERCENT}\\s*%\\s*${off}\\s+Challenges?\\s*${SEPARATOR}\\s*` +
      `${PERCENT}\\s*%\\s*${off}\\s+Instant[\\s-]?Funding\\.?`,
    "i",
  );

const buildSingle = (off: string) =>
  new RegExp(`${PERCENT}\\s*%\\s*${off}\\s+Challenges?\\.?`, "i");

/**
 * Looser pair: the two offers written as two sentences instead of one clause —
 * "70% off Challenges. 40% off Instant Funding." — which is how the /wce hero
 * chips are worded. Only `rewriteOfferRichtextAcrossSpans` uses it, so the pages
 * driven by PAIR_RE keep matching exactly what they matched before.
 */
const buildSplitPair = (off: string) =>
  new RegExp(
    `${PERCENT}\\s*%\\s*${off}\\s+Challenges?\\s*(?:${SEPARATOR}|\\.|;)?\\s*` +
      `${PERCENT}\\s*%\\s*${off}\\s+Instant[\\s-]?Funding\\.?`,
    "i",
  );

/**
 * Mid-sentence pair joined by "or"/"oder", where the sentence continues after
 * it — "The 70% off Challenges or 40% off Instant Funding discount is applied
 * automatically." The connector is captured so it survives the rewrite, and no
 * period is added.
 */
const buildInlinePair = (off: string, connector: string) =>
  new RegExp(
    `${PERCENT}\\s*%\\s*${off}\\s+Challenges?\\s+(${connector})\\s+` +
      `${PERCENT}\\s*%\\s*${off}\\s+Instant[\\s-]?Funding`,
    "i",
  );

/**
 * Coupon lines keep their per-page code (37 distinct values, some with
 * typographic quotes or stray spaces): group 1 lazily captures through the code
 * so it survives the rewrite verbatim.
 *   DE "Im Check-Out den Coupon "TRADEMANIA" einlösen und bis zu 80% sparen."
 *   EN "Enter the coupon code "ALINA" at checkout and save up to 70%."
 */
const COUPON_RE: Record<OfferLocale, RegExp> = {
  de: /(Im\s+Check-?Out\s+den\s+Coupon\s+.*?einlösen\s+und\s+)(?:bis\s+zu\s+)?\d+(?:[.,]\d+)?\s*%\s*sparen/i,
  en: /(Enter\s+(?:the\s+)?coupon\s+code\s+.*?at\s+checkout\s+and\s+)save\s+(?:up\s+to\s+)?\d+(?:[.,]\d+)?\s*%/i,
};

const PAIR_RE: Record<OfferLocale, RegExp> = {
  de: buildPair(DE_OFF),
  en: buildPair(EN_OFF),
};

const SINGLE_RE: Record<OfferLocale, RegExp> = {
  de: buildSingle(DE_OFF),
  en: buildSingle(EN_OFF),
};

const SPLIT_PAIR_RE: Record<OfferLocale, RegExp> = {
  de: buildSplitPair(DE_OFF),
  en: buildSplitPair(EN_OFF),
};

const INLINE_PAIR_RE: Record<OfferLocale, RegExp> = {
  de: buildInlinePair(DE_OFF, "oder"),
  en: buildInlinePair(EN_OFF, "or"),
};

// German is checked first: "80 % Rabatt auf Challenges" must never be read as
// English. The two vocabularies do not overlap, so order only matters as a
// tie-breaker for mixed pages.
const LOCALES: OfferLocale[] = ["de", "en"];

const COPY: Record<
  OfferLocale,
  {
    chip: (percent: number) => string;
    badge: (percent: number) => string;
    sentence: (percent: number) => string;
    listItem: (percent: number) => string;
    coupon: (percent: number) => string;
    /** Same offer mid-sentence, keeping the connector the copy already uses. */
    phrase: (percent: number, connector: string) => string;
    /** Coupon line without the "immer/always" framing. */
    couponPlain: (percent: number) => string;
    /** heroContent second paragraph — the firm claim follows the funding type. */
    firm: Record<AffiliateFundingType, string>;
    tagline: string;
  }
> = {
  de: {
    chip: (p) => `${p}% Rabatt auf alle Produkte`,
    badge: (p) => `${p}% auf alles`,
    sentence: (p) => `${p}% Rabatt auf Instant Funding und Challenges.`,
    listItem: (p) => `${p}% Rabatt auf Instant Funding und Challenges`,
    coupon: (p) => `immer ${p}% sparen`,
    phrase: (p, connector) =>
      `${p}% Rabatt auf Instant Funding ${connector} Challenges`,
    couponPlain: (p) => `${p}% sparen`,
    firm: {
      futures: "Und das bei der #1 Futures-Prop-Firma:",
      crypto: "Und das bei der #1 Krypto-Prop-Firma:",
    },
    tagline: "Klare Regeln, faire Preise und ultraschnelle Payouts!",
  },
  en: {
    chip: (p) => `${p}% off all products`,
    badge: (p) => `${p}% off everything`,
    sentence: (p) => `${p}% off Instant Funding and Challenges.`,
    listItem: (p) => `${p}% off Instant Funding and Challenges`,
    coupon: (p) => `always save ${p}%`,
    phrase: (p, connector) => `${p}% off Instant Funding ${connector} Challenges`,
    couponPlain: (p) => `save ${p}%`,
    firm: {
      futures: "All from the #1 Futures Prop Firm:",
      crypto: "All from the #1 Crypto Prop Firm:",
    },
    tagline: "Clear rules, fair pricing, and ultra-fast payouts!",
  },
};

/**
 * Which language the page's stored offer copy is written in, or undefined when
 * none of the old wording is present (nothing to rewrite).
 *
 * Detected from the copy itself rather than `settings.language`, because the
 * flag is unreliable: /tradingiq is tagged `de` but is written in English.
 */
export function resolveOfferLocale(
  texts: Array<string | undefined | null>,
): OfferLocale | undefined {
  const present = texts.filter((text): text is string => !!text);
  return LOCALES.find((locale) =>
    present.some(
      (text) => PAIR_RE[locale].test(text) || SINGLE_RE[locale].test(text),
    ),
  );
}

/** Hero badge text. Lives in JSX because the Sanity field is only a toggle. */
export const newOfferBadgeText = (percent: number, locale: OfferLocale) =>
  COPY[locale].badge(percent);

const span = (key: string, text: string, marks: string[] = []) => ({
  _type: "span",
  _key: `new-offer-${key}`,
  text,
  marks,
});

const paragraph = (key: string, children: object[]) =>
  ({
    _type: "block",
    _key: `new-offer-${key}`,
    style: "normal",
    markDefs: [],
    children,
  }) as unknown as PortableTextBlock;

/**
 * heroContent is rebuilt rather than clause-rewritten: the 40 pages hold as many
 * hand-written variants of the same paragraph (#1 CFD-/Forex-/Krypto-/Futures-
 * Prop-Firma, some with a trailing "$1 zu starten" sentence), and the new offer
 * needs one consistent block everywhere. The firm claim follows the page's
 * funding type instead of whatever the document happens to say.
 *
 * Shape mirrors what the documents already store — two paragraphs, the second
 * one bold claim + plain tagline — so the hero renders exactly as before.
 */
export function newOfferHeroContent(
  percent: number,
  locale: OfferLocale,
  fundingType: AffiliateFundingType,
): PortableTextBlock[] {
  const copy = COPY[locale];
  return [
    paragraph("offer", [span("offer", copy.sentence(percent), ["strong"])]),
    paragraph("firm", [
      span("firm", `${copy.firm[fundingType]}\n`, ["strong"]),
      span("tagline", copy.tagline),
    ]),
  ];
}

/** Chip is replaced wholesale — the new copy names no individual product. */
export function rewriteOfferChip<T extends string | undefined | null>(
  text: T,
  percent: number | undefined,
  locale: OfferLocale,
): T {
  if (!text || percent == null) return text;
  if (!PAIR_RE[locale].test(text) && !SINGLE_RE[locale].test(text)) return text;
  return COPY[locale].chip(percent) as T;
}

/** Exclusive list item is replaced wholesale. */
export function rewriteOfferListItem<T extends string | undefined | null>(
  text: T,
  percent: number | undefined,
  locale: OfferLocale,
): T {
  if (!text || percent == null || !PAIR_RE[locale].test(text)) return text;
  return COPY[locale].listItem(percent) as T;
}

/**
 * Clause-level swap, so per-page tails survive — e.g.
 * "…Instant Funding. Und die Möglichkeit für $1 zu starten!" keeps its second
 * sentence, and lastText keeps its " Jetzt sichern." / " Claim yours now.".
 */
function rewriteOfferClause(
  text: string,
  percent: number,
  locale: OfferLocale,
): string {
  return text.replace(PAIR_RE[locale], COPY[locale].sentence(percent));
}

function rewriteCouponLine(
  text: string,
  percent: number,
  locale: OfferLocale,
): string {
  return text.replace(
    COUPON_RE[locale],
    (_match, lead: string) => `${lead}${COPY[locale].coupon(percent)}`,
  );
}

function mapRichtext<T extends PortableTextBlock[] | undefined | null>(
  blocks: T,
  transform: (text: string) => string,
): T {
  if (!Array.isArray(blocks)) return blocks;
  return blocks.map((block) => {
    if (!block || block._type !== "block" || !Array.isArray(block.children)) {
      return block;
    }
    return {
      ...block,
      children: block.children.map((child: any) =>
        child && typeof child.text === "string"
          ? { ...child, text: transform(child.text) }
          : child,
      ),
    };
  }) as T;
}

/** heroContent and lastText: swap the split-discount clause, keep the rest. */
export function rewriteOfferRichtext<
  T extends PortableTextBlock[] | undefined | null,
>(blocks: T, percent: number | undefined, locale: OfferLocale): T {
  if (percent == null) return blocks;
  return mapRichtext(blocks, (text) =>
    rewriteOfferClause(text, percent, locale),
  );
}

/**
 * Same swap as rewriteOfferRichtext, but also catches blocks that split the pair
 * across sibling spans — the /wce hero chips store "70% off Challenges. " and
 * "40% off Instant Funding." as two spans with different marks. The unified
 * sentence is one clause, so the matched spans collapse into the first one and
 * the block keeps that span's styling.
 */
export function rewriteOfferRichtextAcrossSpans<
  T extends PortableTextBlock[] | undefined | null,
>(blocks: T, percent: number | undefined, locale: OfferLocale): T {
  if (percent == null || !Array.isArray(blocks)) return blocks;

  const swap = (text: string) =>
    text.replace(SPLIT_PAIR_RE[locale], COPY[locale].sentence(percent));

  return blocks.map((block: any) => {
    if (!block || block._type !== "block" || !Array.isArray(block.children)) {
      return block;
    }
    const children = block.children;

    // One span holds the whole pair: rewrite in place, every span keeps its marks.
    if (
      children.some(
        (child: any) =>
          typeof child?.text === "string" && SPLIT_PAIR_RE[locale].test(child.text),
      )
    ) {
      return {
        ...block,
        children: children.map((child: any) =>
          child && typeof child.text === "string"
            ? { ...child, text: swap(child.text) }
            : child,
        ),
      };
    }

    const joined = children
      .map((child: any) => (typeof child?.text === "string" ? child.text : ""))
      .join("");
    if (!SPLIT_PAIR_RE[locale].test(joined)) return block;

    const first =
      children.find((child: any) => typeof child?.text === "string") ?? children[0];
    return { ...block, children: [{ ...first, text: swap(joined) }] };
  }) as T;
}

/**
 * FAQ answers: "The 70% off Challenges or 40% off Instant Funding discount is
 * applied automatically." → "…the 80% off Instant Funding or Challenges
 * discount…". The sentence continues after the offer, so the connector is kept
 * and no period is introduced.
 */
export function rewriteOfferInlinePairRichtext<
  T extends PortableTextBlock[] | undefined | null,
>(blocks: T, percent: number | undefined, locale: OfferLocale): T {
  if (percent == null) return blocks;
  return mapRichtext(blocks, (text) =>
    text.replace(
      INLINE_PAIR_RE[locale],
      (_match, connector: string) => COPY[locale].phrase(percent, connector),
    ),
  );
}

/**
 * Plain-string coupon line — the /wce steps store it as `text`, not richtext:
 * 'Enter coupon code "WCE" at checkout and save up to 70%.' → '…and save 80%.'
 * Drops the "up to" hedge now that a single percentage covers both products.
 */
export function rewriteCouponText<T extends string | undefined | null>(
  text: T,
  percent: number | undefined,
  locale: OfferLocale,
): T {
  if (!text || percent == null) return text;
  return text.replace(
    COUPON_RE[locale],
    (_match, lead: string) => `${lead}${COPY[locale].couponPlain(percent)}`,
  ) as T;
}

/** stepsItems: "bis zu X% sparen" → "immer P% sparen", coupon preserved. */
export function rewriteCouponRichtext<
  T extends PortableTextBlock[] | undefined | null,
>(blocks: T, percent: number | undefined, locale: OfferLocale): T {
  if (percent == null) return blocks;
  return mapRichtext(blocks, (text) =>
    rewriteCouponLine(text, percent, locale),
  );
}
